<template>
  <ak-popover
    overlay
    :placement="placement"
    size="none"
    :class="type === 'swatch' ? 'inline-block' : 'block'"
  >
    <template #trigger>
      <ak-color-picker-preview
        v-if="type === 'swatch'"
        class="ak-color-swatch"
        :color="rgbaString"
      />
      <div v-else class="ak-color-input">
        <ak-pack spacing="tiny" alignment="center" class="one-whole">
          <ak-pack-item shrink>
            <ak-color-picker-preview :color="rgbaString" />
          </ak-pack-item>
          <ak-pack-item grow>
            <ak-color-picker-color-input
              :color="modelHex"
              @input-color="inputControl"
            />
          </ak-pack-item>
        </ak-pack>
      </div>
    </template>

    <template #popover>
      <div
        class="ak-color-picker"
        :class="{ 'ak-color-picker--light': isLightTheme }"
        :style="{ width: totalWidth + 'px' }"
      >
        <div class="ak-color-picker__set">
          <ak-color-picker-saturation
            ref="saturation"
            :color="rgbString"
            :hsv="hsv"
            :size="hueHeight"
            @select-saturation="selectSaturation"
          />

          <ak-color-picker-hue
            ref="hue"
            :hsv="hsv"
            :width="hueWidth"
            :height="hueHeight"
            @select-hue="selectHue"
          />

          <ak-color-picker-alpha
            ref="alpha"
            :color="rgbString"
            :rgba="rgba"
            :width="hueWidth"
            :height="hueHeight"
            @select-alpha="selectAlpha"
          />
        </div>

        <!-- <div
          :style="{ height: previewHeight + 'px' }"
          class="ak-color-picker__show"
        >
          <ak-color-picker-preview
            :color="rgbaString"
            :width="previewWidth"
            :height="previewHeight"
          />

          <ak-color-picker-sucker
            v-if="!suckerHide"
            :sucker-canvas="suckerCanvas"
            :sucker-area="suckerArea"
            @open-sucker="openSucker"
            @select-sucker="selectSucker"
          />
        </div> -->

        <ak-color-picker-color-box
          v-if="showHexValue"
          name="HEX"
          :color="modelHex"
          @input-color="inputHex"
        />

        <ak-color-picker-color-box
          v-if="showRgbaValue"
          name="RGBA"
          :color="modelRgba"
          @input-color="inputRgba"
        />

        <ak-color-picker-colors
          :color="rgbaString"
          :colors-default="colorsDefault"
          :colors-history-key="colorsHistoryKey"
          @select-color="selectColor"
        />
      </div>
    </template>
  </ak-popover>
</template>

<script>
import { defineComponent, ref, computed, watch, nextTick, onMounted } from 'vue'
import config, { defaultColorPickerColors } from '../../utils/config'
import colorPickerMixin from '../../mixins/colorPickerMixin'
import Popover from '../popover/Popover.vue'
import Pack from '../pack/Pack.vue'
import PackItem from '../pack/PackItem.vue'
import Saturation from './Saturation.vue'
import Hue from './Hue.vue'
import Alpha from './Alpha.vue'
import Preview from './Preview.vue'
import ColorBox from './ColorBox.vue'
import ColorInput from './ColorInput.vue'
import Colors from './Colors.vue'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-color-picker />
   */
  name: 'AkColorPicker',

  /**
   * @type {Object}
   * @description A collection of components that this component
   * is dependant on.
   */
  components: {
    [Saturation.name]: Saturation,
    [Hue.name]: Hue,
    [Alpha.name]: Alpha,
    [Preview.name]: Preview,
    [ColorBox.name]: ColorBox,
    [ColorInput.name]: ColorInput,
    [Colors.name]: Colors,
    [Pack.name]: Pack,
    [PackItem.name]: PackItem,
    [Popover.name]: Popover,
  },

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    label: {
      type: String,
      default: undefined,
    },

    color: {
      type: String,
      default: '#000000',
    },

    theme: {
      type: String,
      default: config.defaultColorPickerTheme,
    },

    suckerHide: {
      type: Boolean,
      default: true,
    },

    suckerCanvas: {
      type: null, // HTMLCanvasElement
      default: null,
    },

    suckerArea: {
      type: Array,
      default: () => [],
    },

    colorsDefault: {
      type: Array,
      default: () => defaultColorPickerColors,
    },

    colorsHistoryKey: {
      type: String,
      default: 'vue-colorpicker-history',
    },

    placement: {
      type: String,
      default: 'bottom',
    },

    showHexValue: {
      type: Boolean,
      default: false,
    },

    showRgbaValue: {
      type: Boolean,
      default: true,
    },

    // style of color picker - defaults to input
    // can be 'swatch'
    type: {
      type: String,
      default: null,
      validator: (v) => ['swatch'].indexOf(v) >= 0,
    },
  },

  emits: ['openSucker', 'changeColor'],

  setup(props, { emit }) {
    const hueWidth = ref(15)
    const hueHeight = ref(152)
    const previewHeight = ref(30)
    const modelRgba = ref('')
    const modelHex = ref('')
    const mixin = colorPickerMixin()
    const saturation = ref(null)
    const hue = ref(null)
    const alpha = ref(null)

    const rgbaVars = ref({
      r: 0,
      g: 0,
      b: 0,
      a: 1,
    })

    const hsvVars = ref({
      h: 0,
      s: 0,
      v: 0,
    })

    const isLightTheme = computed(() => {
      return props.theme === 'light'
    })

    const totalWidth = computed(() => {
      return hueHeight.value + (hueWidth.value + 18) * 2
    })

    const previewWidth = computed(() => {
      return (
        totalWidth.value - (props.suckerHide ? 0 : previewHeight.value) - 20
      )
    })

    const rgba = computed(() => {
      return rgbaVars.value
    })

    const hsv = computed(() => {
      return hsvVars.value
    })

    const rgbString = computed(() => {
      return `rgb(${rgbaVars.value.r}, ${rgbaVars.value.g}, ${rgbaVars.value.b})`
    })

    const rgbaStringShort = computed(() => {
      return `${rgbaVars.value.r}, ${rgbaVars.value.g}, ${rgbaVars.value.b}, ${rgbaVars.value.a}`
    })

    const rgbaString = computed(() => {
      return `rgba(${rgbaStringShort.value})`
    })

    const hexString = computed(() => {
      return mixin.rgb2hex(rgba.value, true)
    })

    const assignValues = ({ r, g, b, a, h, s, v }) => {
      Object.assign(rgbaVars.value, { r, g, b, a })
      Object.assign(hsvVars.value, { h, s, v })
    }

    const inputControl = (color) => {
      assignValues(mixin.setColorValue(color))
      modelHex.value = color
      modelRgba.value = rgbaStringShort.value
      if (saturation.value && hue.value) {
        nextTick(() => {
          saturation.value.renderColor()
          saturation.value.renderSlide()
          hue.value.renderSlide()
        })
      }
    }

    const inputHex = (color) => {
      assignValues(mixin.setColorValue(color))
      modelHex.value = color
      modelRgba.value = rgbaStringShort.value
      nextTick(() => {
        saturation.value.renderColor()
        saturation.value.renderSlide()
        hue.value.renderSlide()
      })
    }

    const inputRgba = (color) => {
      assignValues(mixin.setColorValue(color))
      modelHex.value = hexString.value
      modelRgba.value = color
      nextTick(() => {
        saturation.value.renderColor()
        saturation.value.renderSlide()
        hue.value.renderSlide()
      })
    }

    const openSucker = (isOpen) => {
      emit('openSucker', isOpen)
    }

    const setText = () => {
      modelHex.value = hexString.value
      modelRgba.value = rgbaStringShort.value
    }

    const selectSaturation = (color) => {
      assignValues(mixin.setColorValue(color))
      setText()
    }

    const selectHue = (color) => {
      assignValues(mixin.setColorValue(color))
      setText()
      nextTick(() => {
        saturation.value.renderColor()
        saturation.value.renderSlide()
      })
    }

    const selectAlpha = (a) => {
      rgbaVars.value.a = a
      setText()
    }

    const selectSucker = (color) => {
      assignValues(mixin.setColorValue(color))
      setText()
      nextTick(() => {
        saturation.value.renderColor()
        saturation.value.renderSlide()
        hue.value.renderSlide()
      })
    }

    const selectColor = (color) => {
      assignValues(mixin.setColorValue(color))
      setText()
      nextTick(() => {
        saturation.value.renderColor()
        saturation.value.renderSlide()
        hue.value.renderSlide()
      })
    }

    watch(
      rgba,
      () => {
        emit('changeColor', {
          rgba: rgba.value,
          hsv: hsv.value,
          hex: modelHex.value,
        })
      },
      {
        deep: true,
      }
    )

    onMounted(() => {
      assignValues(mixin.setColorValue(props.color))
      setText()
    })

    return {
      ...rgbaVars.value,
      ...hsvVars.value,
      ...mixin,
      hueWidth,
      hueHeight,
      previewHeight,
      modelRgba,
      modelHex,
      isLightTheme,
      totalWidth,
      previewWidth,
      rgba,
      hsv,
      rgbString,
      rgbaStringShort,
      rgbaString,
      hexString,
      selectSaturation,
      selectHue,
      selectAlpha,
      inputControl,
      inputHex,
      inputRgba,
      setText,
      openSucker,
      selectSucker,
      selectColor,
      saturation,
      hue,
      alpha,
    }
  },
})
</script>

<style lang="scss" rel="stylesheet/scss">
@import '../../scss/_settings.scss';
.ak-color-picker {
  $self: &;
  padding: 10px;
  background: $color-grey-dark;
  border-radius: $base-border-radius;
  z-index: 1;

  &--light {
    background: #fff;
  }

  canvas {
    vertical-align: top;
  }

  &__set {
    display: flex;
  }

  &__show {
    margin-top: 8px;
    display: flex;
  }
}

.ak-color-swatch {
  height: $input-total-height;
  width: $input-total-height;
  border-radius: 50%;
}

.ak-color-input {
  display: flex;
  align-items: center;
  background-color: #fff;
  color: $color-text;
  border: 1px solid $border-input-color;
  border-radius: $base-border-radius;
  box-shadow: $form-box-shadow;
  font-family: $base-font-family;
  font-size: $base-font-size;
  width: 100%;
  padding-left: $spacing-tiny;
  min-height: $input-total-height;
  border-radius: $base-border-radius;
  transition-property: border-color, box-shadow, background-color, color;
  transition-duration: $transition-time;

  &::-webkit-input-placeholder {
    /* Chrome/Opera/Safari */
    color: $color-grey-light;
  }
  &::-moz-placeholder {
    /* Firefox 19+ */
    color: $color-grey-light;
  }
  &:-ms-input-placeholder {
    /* IE 10+ */
    color: $color-grey-light;
  }
  &:-moz-placeholder {
    /* Firefox 18- */
    color: $color-grey-light;
  }

  &:hover {
    border-color: darken($border-input-color, 15%);
  }

  &:focus,
  .ak-popover--is-open > .ak-popover__trigger & {
    border-color: $color-action;
    box-shadow: $form-box-shadow-focus;
    outline: none;
  }

  &[read-only],
  &[readonly],
  &.is-readonly,
  &[disabled],
  &.is-disabled {
    background-color: $color-grey-lightest;
  }

  &[disabled],
  &.is-disabled {
    color: rgba($color-text, 0.625);
    border-color: lighten($border-input-color, 10%);
  }
}
</style>
